import { useEffect, useState, type ReactNode } from 'react';

const DURATION = 200;

/**
 * Lightweight fade wrapper for subpage routes.
 * - Fades in on mount (opacity 0 → 1)
 * - Exposes a `leaving` prop to fade out (opacity 1 → 0) before navigation
 * - Respects prefers-reduced-motion
 */
export default function PageFade({ leaving, children }: { leaving?: boolean; children: ReactNode }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Ensure subpage lands at top on mount (route change should not inherit
    // previous scroll position).
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch { /* ignore */ }
    let reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { /* ignore */ }
    if (reduced) {
      setShown(true);
      return;
    }
    // Two rAFs so the initial opacity:0 style is committed before flipping.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setShown(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  const visible = shown && !leaving;

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${DURATION}ms ease`,
        willChange: 'opacity',
      }}
    >
      {children}
    </div>
  );
}

export const PAGE_FADE_MS = DURATION;